"use client";

// ===================================================================
// Sidebar scope toggle — the one row that replaced three stacked ones.
//
// Picks WHICH slice of the app menu is on screen: everything, one platform,
// one family, or one of the folders the reader made. Only the chosen scope's
// strip renders under it, so the chrome above the first menu item went from
// three permanent filter rows to this row plus at most one.
//
// It shows the scope, not the choice inside it — the strip below does that.
// Labels follow the neighbouring selectors and stay inline rather than moving
// into the locale files, because all three controls read as one unit and a
// half-migrated unit is harder to keep consistent than an unmigrated one.
// ===================================================================

import { FolderOpen, Layers, LayoutGrid, Shapes } from "lucide-react";
import type { SidebarScope } from "@/lib/sidebar/useSidebarScope";
import type { Language } from "@/contexts/SettingsContext";

interface Props {
  scope: SidebarScope;
  onChange: (scope: SidebarScope) => void;
  language: Language;
  /** "compact" drops the labels and keeps the icons — the apps rail. */
  variant?: "full" | "compact";
  className?: string;
}

const LABELS: Record<SidebarScope, Record<"he" | "en" | "ru", string>> = {
  all: { he: "הכל", en: "All", ru: "Все" },
  platforms: { he: "פלטפורמות", en: "Platforms", ru: "Платформы" },
  families: { he: "משפחות", en: "Families", ru: "Семейства" },
  folders: { he: "התיקיות שלי", en: "My folders", ru: "Мои папки" },
};

const ICONS: Record<SidebarScope, React.ElementType> = {
  all: LayoutGrid,
  platforms: Layers,
  families: Shapes,
  folders: FolderOpen,
};

const ORDER: SidebarScope[] = ["all", "platforms", "families", "folders"];

export function SidebarScopeToggle({
  scope,
  onChange,
  language,
  variant = "full",
  className = "",
}: Props) {
  const lang: "he" | "en" | "ru" = language === "he" || language === "ru" ? language : "en";
  const compact = variant === "compact";

  return (
    <div
      data-cc-id="sidebar.scope-toggle"
      className={`flex items-center gap-0.5 rounded-lg bg-[var(--bg-muted)] p-0.5 ${className}`}
      role="group"
      aria-label={language === "he" ? "בורר אזור בתפריט" : "Menu scope selector"}
    >
      {ORDER.map((id) => {
        const Icon = ICONS[id];
        const label = LABELS[id][lang];
        const isActive = scope === id;
        return (
          <button
            key={id}
            type="button"
            onClick={() => onChange(id)}
            aria-pressed={isActive}
            title={label}
            className={`min-w-0 flex-1 rounded-md font-medium transition-colors ${
              compact ? "p-1" : "px-1 py-1 text-[0.625rem]"
            } ${
              isActive
                ? "bg-[var(--cc-accent-600-20)] text-[var(--cc-accent-300)]"
                : "text-[var(--fg-muted)] hover:text-[var(--fg-default)]"
            }`}
          >
            <span className="flex min-w-0 items-center justify-center gap-1">
              <Icon className="h-3 w-3 shrink-0" />
              {!compact && <span className="truncate">{label}</span>}
            </span>
          </button>
        );
      })}
    </div>
  );
}
